import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Minus, Plus, Trash2, Pencil, ShoppingBag } from "lucide-react";
import { useCart, gbp } from "../context/CartContext";

export default function BasketSheet({ open, onOpenChange, settings, onEdit }) {
  const navigate = useNavigate();
  const { items, mode, incQty, decQty, removeLine, subtotal } = useCart();

  let deliveryCharge = 0;
  let belowMin = false;
  if (mode === "delivery" && settings) {
    deliveryCharge = Number(settings.delivery_charge || 0);
    const freeTh = Number(settings.free_delivery_threshold || 0);
    if (freeTh && subtotal >= freeTh) deliveryCharge = 0;
    belowMin = subtotal < Number(settings.min_delivery_order || 0);
  }
  const serviceCharge = Number(settings?.service_charge || 0);
  const total = subtotal + deliveryCharge + serviceCharge;

  const goCheckout = () => { onOpenChange(false); navigate("/checkout"); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="basket-sheet">
        <SheetHeader className="p-5 border-b">
          <SheetTitle className="font-head text-2xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-brand-teal" /> Your basket
          </SheetTitle>
          <span className="text-sm text-brand-dark/50 capitalize">{mode}</span>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {items.length === 0 && (
            <div className="text-center py-16 text-brand-dark/40">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Your basket is empty</p>
            </div>
          )}
          {items.map((l) => (
            <div key={l.lineId} data-testid={`basket-line-${l.item_id}`} className="rounded-2xl border border-brand-dark/10 p-3 bg-white">
              <div className="flex justify-between gap-2">
                <div className="flex-1">
                  <p className="font-semibold text-brand-dark text-sm">{l.name}</p>
                  {l.extras.map((e) => (
                    <p key={e.id} className="text-xs text-brand-dark/50">+ {e.name}{e.price > 0 ? ` (${gbp(e.price)})` : ""}</p>
                  ))}
                  {l.notes && <p className="text-xs text-red-500 mt-0.5">Note: {l.notes}</p>}
                </div>
                <p className="font-bold text-brand-dark text-sm whitespace-nowrap">{gbp(l.unit_price * l.qty)}</p>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-3 bg-brand-dark/5 rounded-full p-1">
                  <button data-testid={`basket-dec-${l.item_id}`} onClick={() => decQty(l.lineId)} className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-sm active:scale-90 transition-transform">
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-sm font-bold w-4 text-center">{l.qty}</span>
                  <button data-testid={`basket-inc-${l.item_id}`} onClick={() => incQty(l.lineId)} className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-sm active:scale-90 transition-transform">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => onEdit(l)} className="w-8 h-8 rounded-full flex items-center justify-center text-brand-dark/50 hover:bg-brand-dark/5" data-testid={`basket-edit-${l.item_id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => removeLine(l.lineId)} className="w-8 h-8 rounded-full flex items-center justify-center text-red-500 hover:bg-red-50" data-testid={`basket-remove-${l.item_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t p-5 space-y-2 bg-white">
            <div className="flex justify-between text-sm text-brand-dark/70"><span>Subtotal</span><span>{gbp(subtotal)}</span></div>
            {mode === "delivery" && (
              <div className="flex justify-between text-sm text-brand-dark/70">
                <span>Delivery</span><span>{deliveryCharge === 0 ? "Free" : gbp(deliveryCharge)}</span>
              </div>
            )}
            {serviceCharge > 0 && (
              <div className="flex justify-between text-sm text-brand-dark/70">
                <span>Online Ordering Fee</span><span>{gbp(serviceCharge)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold text-brand-dark pt-1"><span>Total</span><span data-testid="basket-total">{gbp(total)}</span></div>
            {belowMin && (
              <p className="text-xs text-red-500 text-center" data-testid="min-order-warning">
                Minimum delivery order is {gbp(settings.min_delivery_order)}. Add {gbp(settings.min_delivery_order - subtotal)} more.
              </p>
            )}
            <Button data-testid="checkout-btn" onClick={goCheckout} disabled={belowMin}
              className="w-full h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-dark text-white text-base font-bold active:scale-[0.98] transition-transform">
              Checkout · {gbp(total)}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
